import { useEffect, useState } from "react";

/**
 * SSR-safe "is this visitor signed in" check for the marketing site's chrome.
 *
 * 123yb692rq2: a signed-in visitor still saw "Login" and "Join for free" in `Navigation.tsx`,
 * and clicking either did nothing — the nav had no idea the visitor was authenticated. This
 * hook is what tells it.
 *
 * `Navigation` renders on every marketing page — homepage, blog, every `/discover` hub — so
 * whatever this hook does runs in the one bundle every visitor downloads. Two constraints
 * follow from that, both already established elsewhere in this bundle (HomeMarketing.tsx's
 * header comment, and the `isPhone` doc comment on `usePlanGate` in planGate.tsx):
 *
 * - Starts `false` on the server render and the first client render, so the two agree and
 *   hydration never mismatches. The real answer arrives after mount, once Firebase has had a
 *   chance to report an auth state.
 * - Never a static `import "components/firebase"` (or `useUser`/`useAuthState`). That would
 *   pull Firebase auth — and the whole user-query graph behind it — into the entry bundle for
 *   every visitor, signed in or not, and firebase reads browser globals that don't exist during
 *   SSR. The import below happens only inside the effect, after mount, exactly like
 *   HomeMarketing.tsx's own logged-in redirect. Do not hoist it back to a top-level import.
 */
export function useIsSignedIn(): boolean {
	const [isSignedIn, setIsSignedIn] = useState(false);

	useEffect(() => {
		let cancelled = false;
		let unsub: (() => void) | undefined;
		void (async () => {
			try {
				const firebaseModule = await import("../../firebase");
				const auth = firebaseModule.auth;
				if (!auth || cancelled) return;
				unsub = auth.onAuthStateChanged((fbUser) => {
					if (cancelled) return;
					setIsSignedIn(!!fbUser);
				});
			} catch {
				// firebase failed to load — leave the signed-out default as-is
			}
		})();
		return () => {
			cancelled = true;
			unsub?.();
		};
	}, []);

	return isSignedIn;
}

export default useIsSignedIn;
